import React, { CSSProperties, useId, useState } from 'react';
import { TCoreImageOptions } from '../types/options';
import styles from './image.module.css';

export type TColorSvgImageProps = {
    src: string;
    spinnerSize: number | string;
    dataTestId?: string;
    spinnerDelay?: number;
    defaultSrc?: string;
} & Pick<TCoreImageOptions, 'width' | 'height' | 'borderRadius' | 'iconColor' | 'iconColorDisabled' | 'isDisabled'>;

const DEFAULT_SPINNER_DELAY_MS = 300;

// True when the browser already has the resource in its HTTP cache, so it paints synchronously with no spinner flash.
const isImageCached = (src: string): boolean => {
    if (typeof window === 'undefined' || !src) return false;
    const img = new window.Image();
    img.src = src;
    return img.complete;
};

/**
 * Renders a single-color SVG icon by masking a solid-colored <rect> with the icon's alpha channel.
 *
 * The icon is loaded natively by the browser via <image href> (and a hidden <img> probe used purely for
 * load/error signalling) — there is no fetch() / Cache API involved, so the browser's own HTTP cache is reused.
 * Coloring is done with `mask-type: alpha`: opaque pixels of the source icon reveal the colored rect, transparent
 * pixels stay hidden, which recolors any icon regardless of its own fills.
 */
const ColorSvgImage = ({
    src,
    iconColor,
    iconColorDisabled,
    isDisabled,
    width,
    height,
    borderRadius,
    dataTestId,
    spinnerSize,
    spinnerDelay = DEFAULT_SPINNER_DELAY_MS,
    defaultSrc,
}: TColorSvgImageProps) => {
    // useId() ids contain colons which are invalid in url(#id) references — strip them for a safe mask id.
    const maskId = `csi-mask-${useId().replace(/:/g, '')}`;

    const [resolvedSrc, setResolvedSrc] = useState(src);
    const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(() => (isImageCached(src) ? 'loaded' : 'loading'));
    const [prevSrc, setPrevSrc] = useState(src);
    // Intrinsic dimensions reported by the loaded SVG, used when width/height are left as 'auto'.
    const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

    // Reset on src change during render (no sync setState in effects).
    if (src !== prevSrc) {
        setPrevSrc(src);
        setResolvedSrc(src);
        setStatus(isImageCached(src) ? 'loaded' : 'loading');
        setNaturalSize(null);
    }

    const loading = status === 'loading';
    const colorForIcon = isDisabled ? iconColorDisabled : iconColor;

    // 'auto' (the default) defers to the dimensions the server SVG declares; any explicit value wins over them.
    const resolveDimension = (value: TCoreImageOptions['width'], natural?: number) => (value === 'auto' && natural ? natural : value);
    const effectiveWidth = resolveDimension(width, naturalSize?.width);
    const effectiveHeight = resolveDimension(height, naturalSize?.height);

    const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (naturalWidth && naturalHeight) {
            setNaturalSize({ width: naturalWidth, height: naturalHeight });
        }
        setStatus('loaded');
    };

    const handleError = () => {
        // On load failure fall back to defaultSrc once, otherwise render an empty placeholder box.
        if (defaultSrc && resolvedSrc !== defaultSrc) {
            setResolvedSrc(defaultSrc);
            setStatus(isImageCached(defaultSrc) ? 'loaded' : 'loading');
        } else {
            setStatus('error');
        }
    };

    if (status === 'error') {
        return <div data-test={dataTestId} style={{ width: effectiveWidth, height: effectiveHeight, borderRadius } as CSSProperties} />;
    }

    return (
        <div data-test={dataTestId} style={{ width: effectiveWidth, height: effectiveHeight, borderRadius, position: 'relative', flexShrink: 0 } as CSSProperties}>
            {/* Hidden probe: reliable load/error signal across browsers and caches; reuses the same URL the mask loads. */}
            <img src={resolvedSrc} alt="" aria-hidden style={{ display: 'none' }} onLoad={handleLoad} onError={handleError} />

            <svg width="100%" height="100%" aria-hidden style={{ display: 'block', borderRadius, visibility: loading ? 'hidden' : 'visible' } as CSSProperties}>
                <mask id={maskId} style={{ maskType: 'alpha' } as CSSProperties}>
                    <image href={resolvedSrc} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" />
                </mask>
                <rect width="100%" height="100%" fill={colorForIcon} mask={`url(#${maskId})`} />
            </svg>

            {loading && (
                <div
                    className={styles.spinnerContainer}
                    style={
                        {
                            '--spinner-delay': `${spinnerDelay}ms`,
                        } as CSSProperties
                    }
                >
                    <div className={styles.spinner} style={{ width: spinnerSize, height: spinnerSize }} />
                </div>
            )}
        </div>
    );
};

export { ColorSvgImage };
